import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';

import { RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PageTitleService } from '../../core/services/page-title.service';
import { MemberApiService } from '../../core/services/member-api.service';
import { environment } from '../../../environments/environment';

type CarouselKey = 'first' | 'latest';

@Component({
    selector: 'app-counsel-photo',
    imports: [RouterLink],
    templateUrl: './counsel-photo.component.html',
    styleUrl: './counsel-photo.component.css'
})
export class CounselPhotoComponent implements OnInit, OnDestroy {
  private pageTitle = inject(PageTitleService);
  private memberApi = inject(MemberApiService);
  private http = inject(HttpClient);

  firstImages: string[] = [];
  latestImages: string[] = [];
  firstIndex = 0;
  latestIndex = 0;
  slidesPerView = 1;
  loading = true;

  private destroyed = false;
  private pointerStart: { x: number; pointerId: number; carousel: CarouselKey } | null = null;

  async ngOnInit(): Promise<void> {
    this.pageTitle.set('fa s-icon-image', 'ビフォーアフター');
    this.updateSlidesPerView(this.viewportWidth());

    try {
      const data = await this.memberApi.getCounsel('self');
      if (!Array.isArray(data) || data.length === 0) return;

      const first = data[data.length - 1];
      const latest = data[0];
      let firstImages: string[];
      let latestImages: string[];

      if (first.id === latest.id) {
        firstImages = await this.loadImages(first);
        latestImages = [...firstImages];
      } else {
        [firstImages, latestImages] = await Promise.all([
          this.loadImages(first),
          this.loadImages(latest),
        ]);
      }

      if (this.destroyed) {
        this.revokeImages([...firstImages, ...latestImages]);
        return;
      }

      this.firstImages = firstImages;
      this.latestImages = latestImages;
    } finally {
      if (!this.destroyed) this.loading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.revokeImages([...this.firstImages, ...this.latestImages]);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateSlidesPerView(this.viewportWidth());
  }

  updateSlidesPerView(width: number): void {
    this.slidesPerView = width >= 1080 ? 4 : width >= 767 ? 2 : 1;
    this.firstIndex = this.clampIndex('first', this.firstIndex);
    this.latestIndex = this.clampIndex('latest', this.latestIndex);
  }

  previous(carousel: CarouselKey): void {
    this.setIndex(carousel, this.activeIndex(carousel) - 1);
  }

  next(carousel: CarouselKey): void {
    this.setIndex(carousel, this.activeIndex(carousel) + 1);
  }

  goTo(carousel: CarouselKey, index: number): void {
    this.setIndex(carousel, index);
  }

  canMovePrevious(carousel: CarouselKey): boolean {
    return this.activeIndex(carousel) > 0;
  }

  canMoveNext(carousel: CarouselKey): boolean {
    return this.activeIndex(carousel) < this.maxIndex(carousel);
  }

  carouselStops(carousel: CarouselKey): number[] {
    return Array.from({ length: this.maxIndex(carousel) + 1 }, (_, index) => index);
  }

  trackTransform(carousel: CarouselKey): string {
    const index = this.activeIndex(carousel);
    const offset = index * (100 / this.slidesPerView);
    const gapOffset = this.slidesPerView === 1 ? 0 : index * (5 / this.slidesPerView);
    return `translate3d(calc(-${offset}% - ${gapOffset}px), 0, 0)`;
  }

  isSlideVisible(carousel: CarouselKey, index: number): boolean {
    const firstVisible = this.activeIndex(carousel);
    return index >= firstVisible && index < firstVisible + this.slidesPerView;
  }

  onCarouselKeydown(event: KeyboardEvent, carousel: CarouselKey): void {
    if (event.key === 'ArrowLeft') {
      this.previous(carousel);
    } else if (event.key === 'ArrowRight') {
      this.next(carousel);
    } else if (event.key === 'Home') {
      this.goTo(carousel, 0);
    } else if (event.key === 'End') {
      this.goTo(carousel, this.maxIndex(carousel));
    } else {
      return;
    }
    event.preventDefault();
  }

  onPointerDown(event: PointerEvent, carousel: CarouselKey): void {
    if (!event.isPrimary || this.maxIndex(carousel) === 0) return;
    if ((event.target as HTMLElement | null)?.closest?.('button')) return;
    this.pointerStart = { x: event.clientX, pointerId: event.pointerId, carousel };
    (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
  }

  onPointerUp(event: PointerEvent, carousel: CarouselKey): void {
    const start = this.pointerStart;
    this.pointerStart = null;
    if (!start || start.pointerId !== event.pointerId || start.carousel !== carousel) return;

    const distance = event.clientX - start.x;
    if (Math.abs(distance) < 40) return;
    if (distance < 0) this.next(carousel);
    else this.previous(carousel);
  }

  onPointerCancel(): void {
    this.pointerStart = null;
  }

  private async loadImages(counsel: any): Promise<string[]> {
    const requests: Array<Promise<string | null>> = [];
    for (let i = 1; i <= 4; i++) {
      const imgKey = `uploadedImg${i}`;
      if (counsel[imgKey]) {
        const url = `${environment.apiBaseUrl}/counsel/${counsel.id}/picture/${counsel[imgKey]}`;
        requests.push(this.loadImage(url));
      }
    }
    const results = await Promise.all(requests);
    return results.filter((url): url is string => url !== null);
  }

  private async loadImage(url: string): Promise<string | null> {
    try {
      const blob = await firstValueFrom(this.http.get(url, { responseType: 'blob' }));
      return blob.size > 0 ? URL.createObjectURL(blob) : null;
    } catch {
      return null;
    }
  }

  private images(carousel: CarouselKey): string[] {
    return carousel === 'first' ? this.firstImages : this.latestImages;
  }

  private activeIndex(carousel: CarouselKey): number {
    return carousel === 'first' ? this.firstIndex : this.latestIndex;
  }

  private maxIndex(carousel: CarouselKey): number {
    return Math.max(0, this.images(carousel).length - this.slidesPerView);
  }

  private clampIndex(carousel: CarouselKey, index: number): number {
    return Math.min(Math.max(0, index), this.maxIndex(carousel));
  }

  private setIndex(carousel: CarouselKey, index: number): void {
    const nextIndex = this.clampIndex(carousel, index);
    if (carousel === 'first') this.firstIndex = nextIndex;
    else this.latestIndex = nextIndex;
  }

  private revokeImages(images: string[]): void {
    new Set(images).forEach(url => URL.revokeObjectURL(url));
  }

  private viewportWidth(): number {
    return typeof window === 'undefined' ? 0 : window.innerWidth;
  }
}
